import { useCallback, useRef, useState } from 'react';
import { narrativeStream, NarrativeSSEvent, NarrativeDonePayload } from '@lib/narrative-stream';
import { getAccessToken } from '@services/authentication';
import * as Sentry from '@sentry/react';

export function useNarrativeStream(url: string) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [narrativeVersionId, setNarrativeVersionId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isStreamingRef = useRef(false);
  const controllerRef = useRef<AbortController | null>(null);

  const send = useCallback(
    async (tripProfileId: string, inputParams: Record<string, unknown> = {}) => {
      if (isStreamingRef.current) return;
      isStreamingRef.current = true;
      setIsStreaming(true);
      setStatusMessage(null);
      setDownloadUrl(null);
      setNarrativeVersionId(null);
      setErrorMessage(null);
      const controller = new AbortController();
      controllerRef.current = controller;

      const attempt = async () => {
        const token = (await getAccessToken()) ?? undefined;
        return narrativeStream({
          url,
          body: { tripProfileId, inputParams },
          accessToken: token,
          signal: controller.signal,
          onEvent: (event: NarrativeSSEvent, payload: unknown) => {
            if (event === 'status') {
              const msg = typeof payload === 'string' ? payload : ((payload as { message?: string })?.message ?? '');
              setStatusMessage(msg);
              return;
            }
            if (event === 'done') {
              const data = payload as NarrativeDonePayload;
              setDownloadUrl(data?.downloadUrl ?? null);
              setNarrativeVersionId(data?.narrativeVersionId != null ? String(data.narrativeVersionId) : null);
              return;
            }
            if (event === 'error') {
              const msg =
                typeof payload === 'string' ? payload : ((payload as { message?: string })?.message ?? 'Unknown error');
              setErrorMessage(msg);
              Sentry.captureException(new Error('useNarrativeStream: SSE Error'), { extra: { payload } });
              return;
            }
          },
        });
      };

      try {
        try {
          await attempt();
        } catch (error: unknown) {
          const messageText =
            typeof error === 'object' && error !== null && 'message' in error
              ? String((error as { message?: unknown }).message)
              : '';
          const isAuthError = /401|403|Unauthorized|Forbidden/i.test(messageText);
          if (!controller.signal.aborted && isAuthError) {
            await attempt();
          } else {
            throw error;
          }
        }
      } catch (error: unknown) {
        if (
          !(error && typeof error === 'object' && 'name' in error && (error as { name?: string }).name === 'AbortError')
        ) {
          const msg =
            typeof error === 'object' && error !== null && 'message' in error
              ? String((error as { message?: unknown }).message)
              : 'Stream failed';
          setErrorMessage(msg);
          console.error('useNarrativeStream error:', error);
        }
      } finally {
        try {
          controller.abort();
        } catch {
          // ignore
        }
        isStreamingRef.current = false;
        setIsStreaming(false);
        controllerRef.current = null;
      }
    },
    [url]
  );

  const abort = useCallback(() => {
    controllerRef.current?.abort();
  }, []);

  return { isStreaming, statusMessage, downloadUrl, narrativeVersionId, errorMessage, send, abort };
}
